import { MANAGER_ROLES, type MetricKey, METRIC_LABELS } from '@performance-crm/shared';
import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/lib/auth-context';
import { currentMonth, monthLabel, monthOptions } from '@/lib/months';
import { useDailyReport, useProjects } from '@/lib/queries';
import { useCompanyScope } from '@/lib/useCompanyScope';

const KEYS: MetricKey[] = [
  'CAPTACIONES',
  'LEADS_UNICOS',
  'CONTACTOS_TOTAL',
  'CONTACTOS_EFECTIVOS',
  'CITAS_GENERADAS',
  'CITAS_CONCRETADAS',
  'VISITAS',
  'TRANSITO',
  'SEPARACIONES',
  'VENTAS',
];
const dayLabel = new Intl.DateTimeFormat('es-PE', {
  weekday: 'short',
  day: '2-digit',
  timeZone: 'UTC',
});

export function DailyReportPage() {
  const { user } = useAuth();
  const { companies: allCompanies, active } = useCompanyScope();
  // El reporte muestra cifras de todo el proyecto: solo en las empresas que el usuario gestiona.
  const companies = allCompanies.filter((c) =>
    (user?.memberships ?? []).some(
      (m) => m.companyId === c.companyId && MANAGER_ROLES.includes(m.role),
    ),
  );
  const [companyId, setCompanyId] = useState(() =>
    companies.some((c) => c.companyId === active) ? active : companies[0]?.companyId,
  );
  const [projectId, setProjectId] = useState('');
  // Cambiar la empresa en la cabecera también cambia el reporte (antes se quedaba en la anterior).
  useEffect(() => {
    if (active && active !== companyId && companies.some((c) => c.companyId === active)) {
      setCompanyId(active);
      setProjectId('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo cuando cambia la empresa activa
  }, [active]);
  const [month, setMonth] = useState(currentMonth());
  const { data: projects } = useProjects();
  const { data, isLoading } = useDailyReport(companyId, month, projectId || undefined);
  const today = new Date().toISOString().slice(0, 10);
  if (user && companies.length === 0) return <Navigate to="/" replace />;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Gestión diaria</h1>
        <p className="text-muted-foreground text-sm">
          Días × etapas del mes. Cada etapa cuenta en el día de su propia fecha.
        </p>
      </div>
      <div className="bg-card grid gap-2 rounded-lg border p-3 md:grid-cols-3">
        {companies.length > 1 && (
          <Select
            value={companyId ?? ''}
            onChange={(e) => {
              setCompanyId(e.target.value);
              setProjectId('');
            }}
            aria-label="Empresa"
          >
            {companies.map((c) => (
              <option key={c.companyId} value={c.companyId}>
                {c.companyName}
              </option>
            ))}
          </Select>
        )}
        <Select
          value={projectId}
          onChange={(e) => setProjectId(e.target.value)}
          aria-label="Proyecto"
        >
          <option value="">Todos los proyectos</option>
          {projects
            ?.filter((p) => p.companyId === companyId)
            .map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
        </Select>
        <Select value={month} onChange={(e) => setMonth(e.target.value)} aria-label="Mes">
          {monthOptions().map((m) => (
            <option key={m} value={m}>
              {monthLabel(m)}
            </option>
          ))}
        </Select>
      </div>
      {isLoading && <Skeleton className="h-64 w-full" />}
      {data && (
        <div className="bg-card overflow-x-auto rounded-lg border">
          <table className="w-full text-sm" data-testid="daily-table">
            <thead className="bg-muted/80 text-muted-foreground sticky top-0 text-left text-xs uppercase tracking-wide">
              <tr>
                <th className="px-3 py-2">Día</th>
                {KEYS.map((k) => (
                  <th key={k} className="px-2 py-2 text-right">
                    {METRIC_LABELS[k]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.days.map((d) => {
                const future = d.date > today;
                const empty = KEYS.every((k) => d.metrics[k] === 0);
                return (
                  <tr
                    key={d.date}
                    className={`border-t ${future ? 'text-muted-foreground/50' : ''} ${d.date === today ? 'bg-primary/5 font-medium' : ''}`}
                  >
                    <td className="whitespace-nowrap px-3 py-1">
                      {dayLabel.format(new Date(`${d.date}T00:00:00Z`))}
                    </td>
                    {KEYS.map((k) => (
                      <td
                        key={k}
                        className={`px-2 py-1 text-right ${d.metrics[k] === 0 ? 'text-muted-foreground/60' : ''}`}
                      >
                        {empty && future ? '' : d.metrics[k]}
                      </td>
                    ))}
                  </tr>
                );
              })}
              <tr className="bg-muted/40 border-t font-semibold">
                <td className="px-3 py-2">Total</td>
                {KEYS.map((k) => (
                  <td key={k} className="px-2 py-2 text-right">
                    {data.totals[k]}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
